import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getModulePath, LEAGUE_MODULE_NAMES } from '@leagueplatform/core';
import { useFeatureFlagQuery } from '@leagueplatform/user-profile-api';
import { getUnreadMessageCount } from 'common/unread-message-count.api';

// Shared query key for the unread messaging count. The active plan is resolved
// server-side from the member's session (see the api module), so the key carries
// no plan id — invalidating it after mark-read or after a plan switch is enough
// to refetch the current plan's count.
export const UNREAD_MESSAGE_COUNT_QUERY_KEY = ['unread-message-count'];

// Not exported by the Messaging SDK, which reads the same flag to choose between
// its v1 and v2 thread-list routes.
const ENABLE_MESSAGING_THREADS_V2 = 'enable_messaging_threads_v2';

// The Messaging SDK's thread-list query key (MESSAGING_THREADS_PATH in
// @leagueplatform/messaging-api), used for both thread-list versions. The SDK
// invalidates it once a mark-read request succeeds.
const MESSAGING_THREADS_QUERY_KEY_ROOT = '/v1/messaging-threads';

// How long the arrival text stays in the live region. A live region announces on
// change, so the text only has to be present long enough to be picked up; left
// there, it becomes a stray line anyone reading the page sequentially would meet
// on top of the nav entry that already says the same thing.
const ARRIVAL_ANNOUNCEMENT_LINGER_MS = 5000;

// Messages arrive unannounced; every other refresh (reading a thread, leaving the
// inbox, refocusing the window) needs the member to act first. Pauses in a hidden
// tab. At least twice the connector's 15s GHP timeout, so a slow read finishes or
// fails before the next one starts.
const POLL_INTERVAL_MS = 60000;

// Used while the last read failed, so a struggling GHP is asked less often.
const FAILED_POLL_INTERVAL_MS = 120000;

/**
 * Loads the unread messaging-thread count for the member's active plan. Drives
 * the indicator on the support nav entry, which hides at 0.
 *
 * The route version follows the same flag the Messaging SDK reads, so the count
 * always describes the inbox the member would actually open. The flag is part of
 * the query key, so flipping it refetches rather than serving the other
 * version's cached count.
 */
export const useUnreadMessageCount = () => {
  const { data: isThreadsV2Enabled, isLoading: isFlagLoading } =
    useFeatureFlagQuery(ENABLE_MESSAGING_THREADS_V2);

  return useQuery({
    queryKey: [...UNREAD_MESSAGE_COUNT_QUERY_KEY, isThreadsV2Enabled],
    queryFn: () => getUnreadMessageCount(Boolean(isThreadsV2Enabled)),
    // Waiting for the flag avoids firing a request against the wrong version and
    // then correcting it.
    enabled: !isFlagLoading,
    // One quick retry, then surface the error — the indicator just stays hidden
    // on failure rather than blocking the nav. Mirrors the comm-preferences
    // query.
    retry: 1,
    // Threads are marked read inside the Messaging SDK, which exposes no
    // callback to hook into, so refetch when the window regains focus: after the
    // member reads a thread in the inbox and comes back, the count re-syncs.
    refetchOnWindowFocus: true,
    refetchInterval: (_data, query) =>
      query.state.status === 'error'
        ? FAILED_POLL_INTERVAL_MS
        : POLL_INTERVAL_MS,
  });
};

/**
 * Returns a callback that refetches the count while leaving the current one on
 * screen until the new one lands.
 *
 * For events that make the count merely *stale* — the member has probably read
 * something, so re-check. The existing value is still the same plan's, so
 * holding it avoids blinking the indicator off and straight back on. Contrast
 * {@link useResetUnreadMessageCount}, for events that make it wrong.
 */
const useInvalidateUnreadMessageCount = () => {
  const queryClient = useQueryClient();
  return useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: UNREAD_MESSAGE_COUNT_QUERY_KEY,
      }),
    [queryClient],
  );
};

/**
 * Returns a callback that drops the cached count and fetches it again from
 * scratch.
 *
 * For events that make the count *wrong* rather than stale — a plan switch,
 * where it belongs to an enrolment the member has just left. Invalidating would
 * keep serving the previous plan's count until the refetch landed, flashing that
 * plan's indicator over the new plan's screen; clearing it first means the
 * indicator is simply absent until the real count arrives. Same reasoning as the
 * Masonry node cache being removed rather than invalidated on switch.
 */
export const useResetUnreadMessageCount = () => {
  const queryClient = useQueryClient();
  return useCallback(
    () =>
      queryClient.resetQueries({
        queryKey: UNREAD_MESSAGE_COUNT_QUERY_KEY,
      }),
    [queryClient],
  );
};

/**
 * Refreshes the count once the member navigates away from the messaging screens,
 * on the basis that anything they opened there is now read.
 *
 * Takes the current pathname rather than reading the router itself, so the
 * transition it keys off is directly testable.
 *
 * Without this the indicator goes stale for the rest of the session: threads are
 * marked read inside the Messaging SDK, which exposes no callback to invalidate
 * on, and the nav holding the indicator is mounted once for every private route,
 * so leaving the inbox neither remounts it nor refetches. Only a window
 * blur/focus would clear it.
 */
export const useRefreshUnreadMessageCountAfterReading = (pathname: string) => {
  const invalidateUnreadMessageCount = useInvalidateUnreadMessageCount();
  const messagingPath = getModulePath(LEAGUE_MODULE_NAMES.messaging);
  const wasReadingMessages = useRef(false);

  useEffect(() => {
    // Compared segment by segment rather than as a bare prefix, so a sibling
    // route that merely begins with the same characters is not mistaken for a
    // messaging screen. Matches how the routing layer resolves module paths.
    const isReadingMessages =
      pathname === messagingPath || pathname.startsWith(`${messagingPath}/`);

    if (wasReadingMessages.current && !isReadingMessages) {
      invalidateUnreadMessageCount();
    }

    wasReadingMessages.current = isReadingMessages;
  }, [pathname, messagingPath, invalidateUnreadMessageCount]);
};

/**
 * Refreshes the count as soon as the Messaging SDK marks a thread read.
 *
 * The SDK has no callback for a read, but it shares this app's query client and
 * invalidates its thread-list query once the mark-read request succeeds, so the
 * indicator clears straight after reading instead of at the next poll. Sending a
 * message or opening a thread invalidates the same key; refreshing then is
 * harmless.
 */
export const useRefreshUnreadMessageCountOnThreadsChange = () => {
  const queryClient = useQueryClient();
  const invalidateUnreadMessageCount = useInvalidateUnreadMessageCount();

  useEffect(
    () =>
      queryClient.getQueryCache().subscribe((event) => {
        if (
          event.type === 'updated' &&
          event.action.type === 'invalidate' &&
          event.query.queryKey[0] === MESSAGING_THREADS_QUERY_KEY_ROOT
        ) {
          invalidateUnreadMessageCount();
        }
      }),
    [queryClient, invalidateUnreadMessageCount],
  );
};

/**
 * Returns text for a polite live region when unread messages arrive, and an
 * empty string otherwise.
 *
 * A dot appearing is silent to a screen reader, and the indicator's own label
 * only reaches someone who visits the nav entry. This announces the arrival
 * instead — but only on a genuine transition from a *known* zero, because the
 * first resolution comes from `undefined` and announcing that would fire on
 * every page load. Clearing the text on the way back to zero is what lets a
 * later arrival announce again, since a live region only speaks when it changes.
 *
 * The text is also withdrawn shortly after it is set, so it does not outlive the
 * announcement as a stray line in the page.
 */
export const useUnreadMessageArrivalAnnouncement = (
  unreadMessageCount: number | undefined,
  message: string,
) => {
  const [announcement, setAnnouncement] = useState('');
  const previousCount = useRef<number | undefined>(undefined);

  useEffect(() => {
    const hadKnownZero = previousCount.current === 0;
    previousCount.current = unreadMessageCount;

    if (hadKnownZero && (unreadMessageCount ?? 0) > 0) {
      setAnnouncement(message);
    } else if (unreadMessageCount === 0) {
      setAnnouncement('');
    }
  }, [unreadMessageCount, message]);

  useEffect(() => {
    if (!announcement) return undefined;

    const timeout = setTimeout(
      () => setAnnouncement(''),
      ARRIVAL_ANNOUNCEMENT_LINGER_MS,
    );
    return () => clearTimeout(timeout);
  }, [announcement]);

  return announcement;
};
